/**
 * Pure helpers for the sign-in page: view resolution, copy, and validation.
 * Kept free of React so they can be unit tested directly.
 */

/** Which credential step the email resolved to. */
export type LoginStep = "email" | "password" | "sso";

/** Overlay states that replace the credential form. */
export type LoginModalState =
  | "default"
  | "forgotPassword"
  | "resetSent"
  | "incorrectPassword"
  | "mfa";

/** What the card is showing, collapsing step and modal state into one value. */
export type LoginView = LoginStep | "mfa" | "forgotPassword" | "resetSent";

export type ResolvedEnterpriseProvider = {
  provider_id: string;
  type: string;
  label?: string | null;
  enabled?: boolean | null;
};

export type ResolvedAuthConfig = {
  lawFirmId: string | null;
  lawFirmName?: string | null;
  ssoEnabled?: boolean;
  enforceSso?: boolean;
  tenantId?: string | null;
  providers?: ResolvedEnterpriseProvider[];
};

/** Where a signed-out deep link is kept across an SSO redirect. */
export const RETURN_TO_KEY = "loginReturnTo";

type PathLike = { pathname: string; search?: string; hash?: string };

/** The in-app path to come back to after sign-in, or null for one that is not ours. */
export function returnPath(from: unknown): string | null {
  const value =
    typeof from === "string"
      ? from
      : from && typeof from === "object" && "pathname" in from
      ? `${(from as PathLike).pathname}${(from as PathLike).search ?? ""}${
          (from as PathLike).hash ?? ""
        }`
      : null;
  if (
    !value ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.startsWith("/\\")
  )
    return null;
  if (value === "/" || value.startsWith("/login")) return null;
  return value;
}

/**
 * The deep link to return to: the one the route guard passed, remembered so it
 * survives an SSO round trip, or (back from that round trip) the remembered one.
 */
export function rememberReturnPath(
  from: unknown,
  {
    restore,
    storage = sessionStorage
  }: {
    restore: boolean;
    storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  }
): string | null {
  const path = returnPath(from);
  if (path) {
    storage.setItem(RETURN_TO_KEY, path);
    return path;
  }
  if (!restore) {
    storage.removeItem(RETURN_TO_KEY);
    return null;
  }
  return returnPath(storage.getItem(RETURN_TO_KEY));
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim());
}

/** MFA and password reset take over the card; otherwise the step decides. */
export function getLoginView(
  modalState: LoginModalState,
  step: LoginStep
): LoginView {
  if (modalState === "mfa") return "mfa";
  if (modalState === "forgotPassword") return "forgotPassword";
  if (modalState === "resetSent") return "resetSent";
  return step;
}

export const LOGIN_HEADING = "Access Your Workspace";

/** The card title: one per task, shared by the credential steps. */
export function getLoginHeading(view: LoginView): string {
  switch (view) {
    case "mfa":
      return "Verify It's You";
    case "forgotPassword":
      return "Reset Your Password";
    case "resetSent":
      return "Check Your Email";
    default:
      return LOGIN_HEADING;
  }
}

/** The line under a sent reset link; the same on /login and /reset-password. */
export function getResetSentMessage(email?: string | null): string {
  const target = email?.trim();
  return target
    ? `If an account exists for ${target}, a reset link is on its way.`
    : "If an account exists for that email, a reset link is on its way.";
}

/** Subtitle under the heading; crossfades as the view changes. */
export function getLoginSubtitle(
  view: LoginView,
  context: { phoneNumber?: string | null; email?: string | null } = {}
): string {
  switch (view) {
    case "email":
      return "Sign in to continue.";
    case "password":
      return "Welcome back. Enter your password to continue.";
    case "sso":
      return "Your firm signs in with single sign-on.";
    case "mfa":
      return context.phoneNumber
        ? `Enter the code we sent to ${context.phoneNumber}.`
        : "Enter the verification code we sent you.";
    case "forgotPassword":
      return "We'll email you a link to reset your password.";
    case "resetSent":
      return getResetSentMessage(context.email);
    default:
      return "";
  }
}

/** Label for a firm SSO provider button. */
export function getSsoProviderLabel(
  provider: Pick<ResolvedEnterpriseProvider, "label">
): string {
  const label = provider.label?.trim();
  return label ? `Continue with ${label}` : "Continue with SSO";
}

/** SAML providers need a `SAMLAuthProvider`; everything else is generic OAuth/OIDC. */
export function isSamlProvider(
  provider: Pick<ResolvedEnterpriseProvider, "provider_id" | "type">
): boolean {
  const providerId = String(provider.provider_id || "").trim();
  const providerType = String(provider.type || "")
    .trim()
    .toLowerCase();
  return providerType === "saml" || providerId.startsWith("saml.");
}
